import React, { useEffect, useState, Suspense, lazy } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { getTimeAgo } from "../utils/getTimeAgo"; 
import renderPaginationButtons from "../components/pagination";
import ImageWithFallback from "../helpers/ImageWithFallback";
import { IoImageOutline } from "react-icons/io5";
import BackButton from "../components/BackButton";

// Lazy video card component
const LazyVideoCard = ({ video }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const getYouTubeEmbedUrl = (url) => {
    const videoId = url.split('v=')[1]?.split('&')[0];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  const embedUrl = getYouTubeEmbedUrl(video.url);
  const channel = video.channel;
  const channelLogo = channel?.logo_url || "";
  const channelName = channel?.name || "";

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
      {/* Video Player */}
      <div className="relative aspect-video bg-gray-100 dark:bg-gray-700">
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 bg-gray-200 dark:bg-gray-600 animate-pulse"></div>
        )}
        {embedUrl && !imageError ? (
          <iframe
            src={embedUrl}
            title={video.title}
            className={`w-full h-full transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-200 dark:bg-gray-700">
            <IoImageOutline className="w-12 h-12 text-gray-400 dark:text-gray-500" />
            <span className="ml-2 text-gray-500 dark:text-gray-400">Video not available</span>
          </div>
        )}
      </div>

      {/* Video Info */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
          {video.title}
        </h3>
        
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-3">
          <span className="font-medium">{channelName}</span>
          <span>{video.views} views</span>
        </div>

        {/* Channel Logo and Time Info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {channelLogo && (
              <div className="w-8 h-8 flex items-center">
                <ImageWithFallback
                  src={channelLogo}
                  className="w-8 h-8 rounded-full object-cover"
                  fallback={
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      <IoImageOutline className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    </div>
                  }
                />
              </div>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {getTimeAgo(video.created_at)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Skeleton components for loading states
const VideoCardSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden animate-pulse">
    {/* Video Player Skeleton */}
    <div className="relative aspect-video bg-gray-200 dark:bg-gray-700"></div>
    
    {/* Video Info Skeleton */}
    <div className="p-4">
      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3"></div>
      
      <div className="flex items-center justify-between text-sm mb-3">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
        </div>
      </div>
    </div>
  </div>
);

const VideosPageSkeleton = () => (
  <div className="container mx-auto px-4 py-8">
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"></div>
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-40 animate-pulse"></div>
      </div>
      <div className="w-full h-0.5 mb-10 bg-gray-200 dark:bg-gray-700"></div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(12)].map((_, i) => (
          <VideoCardSkeleton key={i} />
        ))}
      </div>

      {/* Pagination Skeleton */}
      <div className="mt-8 flex justify-center">
        <div className="flex space-x-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const VideosPage = () => {
  const [searchParams, setSearchParams] = useSearchParams(); // For URL parameters
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const videosPerPage = 12;
  
  // Get page from URL or default to 1
  const page = parseInt(searchParams.get('page')) || 1;
  
  // Redirect to page 1 if invalid page number
  useEffect(() => {
    const urlPage = searchParams.get('page');
    if (urlPage && (isNaN(parseInt(urlPage)) || parseInt(urlPage) < 1)) {
      setSearchParams({ page: '1' });
    }
  }, [searchParams, setSearchParams]);

  const totalPages = Math.ceil(totalCount / videosPerPage);

  useEffect(() => {
    const fetchVideos = async () => {
      // Show page loading for subsequent pages, full loading for initial load
      const currentPage = parseInt(searchParams.get('page')) || 1;
      if (currentPage === 1) {
        setLoading(true);
      } else {
        setPageLoading(true);
      }
      
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/ytchannels/videos/`, {
          params: {
            page: page,
            page_size: videosPerPage,
          },
        });
        
        if (response.data && response.data.results) {
          setVideos(response.data.results);
          setTotalCount(response.data.count);
          
          // Check if current page exceeds total pages and redirect to last page
          const calculatedTotalPages = Math.ceil(response.data.count / videosPerPage);
          if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
            setSearchParams({ page: calculatedTotalPages.toString() });
            return;
          }
        } else {
          setVideos([]);
          setTotalCount(0);
        }
      } catch (err) {
        console.error("Error fetching videos:", err);
        setError("Failed to load videos. Please try again later.");
      } finally {
        setLoading(false);
        setPageLoading(false);
      }
    };

    fetchVideos();
  }, [page, searchParams]);

  const handlePageChange = (newPage) => {
    setSearchParams({ page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return <VideosPageSkeleton />;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="text-center py-12">
            <p className="text-red-500 dark:text-red-400 text-lg">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl border-b-2 border-gray-800 dark:border-gray-300 pb-2 font-bold text-gray-900 dark:text-white">
            VIDEO
          </h2>
          <BackButton />
        </div>
        <div className="w-full h-0.5 mb-10 bg-gray-200 dark:bg-gray-700"></div>

        {pageLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(videosPerPage)].map((_, i) => (
              <VideoCardSkeleton key={i} />
            ))}
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 text-lg">No videos available at the moment.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video) => (
                <Suspense key={video.id} fallback={<VideoCardSkeleton />}>
                  <LazyVideoCard video={video} />
                </Suspense>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                {renderPaginationButtons({
                  totalPages,
                  currentPage: page,
                  handlePageChange,
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VideosPage;
